import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import ChatPanel from "./ChatPanel";
import { MessageSquare, X, ArrowLeft, Inbox } from "lucide-react";
import { toast } from "sonner";

/**
 * ClientChatBubble — floating button in the bottom-left corner of the agency
 * portal that shows the list of client conversations. Click a client to open
 * the full chat panel in a popup. Back arrow returns to the thread list so
 * you can switch to another client without leaving the Production page.
 *
 * Visible only for admin + approved agency users (same gating as StaffChatBubble).
 */
export default function ClientChatBubble({ user }) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeClient, setActiveClient] = useState(null); // {id, name_salone, referente_nome, referente_cognome}
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/threads");
      const items = data.items || [];
      setThreads(items);
      const total = items.reduce((acc, t) => acc + (t.unread || 0), 0);
      setTotalUnread(total);
    } catch (e) {
      // silent — polling
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  // Reload threads when coming back from an active chat so unread counters are fresh
  useEffect(() => {
    if (!activeClient && open) {
      load();
    }
  }, [activeClient, open, load]);

  const isStaff =
    user?.role === "admin" ||
    (user?.role === "agency" && user?.status === "approved");
  if (!isStaff) return null;

  const openClient = (t) => {
    setActiveClient(t.client);
  };

  const backToList = () => {
    setActiveClient(null);
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="client-chat-bubble"
        className="fixed bottom-6 left-6 z-40 h-14 w-14 rounded-full bg-[var(--mp-primary)] text-white grid place-items-center shadow-brutal-sm hover:scale-105 transition-transform border-2 border-[var(--mp-ink)]"
        title={`Chat clienti${totalUnread ? ` (${totalUnread} non letti)` : ""}`}
      >
        <MessageSquare className="h-6 w-6" />
        {totalUnread > 0 && (
          <span
            data-testid="client-chat-unread-total"
            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] text-[11px] font-bold grid place-items-center border-2 border-[var(--mp-ink)]"
          >
            {totalUnread > 9 ? "9+" : totalUnread}
          </span>
        )}
      </button>

      {/* Popup */}
      {open && (
        <div
          data-testid="client-chat-popup"
          className="fixed bottom-24 left-6 z-40 w-[360px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-8rem)] rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal overflow-hidden flex flex-col"
        >
          <div className="h-14 px-4 flex items-center justify-between bg-[var(--mp-primary)] text-white border-b border-[var(--mp-ink)] shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              {activeClient ? (
                <>
                  <button
                    type="button"
                    onClick={backToList}
                    data-testid="client-chat-back"
                    className="h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center"
                    title="Torna alla lista"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <div className="font-display text-base tracking-tight truncate">
                    {activeClient.name_salone}
                  </div>
                </>
              ) : (
                <>
                  <MessageSquare className="h-4 w-4" />
                  <div className="font-display text-base tracking-tight">
                    Chat clienti
                  </div>
                  {totalUnread > 0 && (
                    <span className="ml-1 text-[10px] font-bold uppercase tracking-[0.15em] bg-[var(--mp-yellow)] text-[var(--mp-ink)] rounded-full px-2 py-0.5">
                      {totalUnread} non letti
                    </span>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="client-chat-close"
              className="h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 grid place-items-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {activeClient ? (
              <ChatPanel
                clientId={activeClient.id}
                title={activeClient.name_salone}
                height="100%"
              />
            ) : (
              <ThreadsList
                items={threads}
                loading={loading}
                onPick={openClient}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ThreadsList({ items, loading, onPick }) {
  if (loading) {
    return (
      <div className="p-6 text-center text-[var(--mp-muted)] text-sm">
        Caricamento…
      </div>
    );
  }
  if (!items || items.length === 0) {
    return (
      <div className="p-6 text-center text-[var(--mp-muted)] text-sm flex flex-col items-center gap-3">
        <Inbox className="h-6 w-6" />
        Nessuna conversazione aperta.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-[var(--mp-border)] overflow-y-auto h-full">
      {items.map((t) => {
        const { client, last_message, unread } = t;
        const initials = (client.name_salone || "?")
          .split(" ")
          .map((s) => s[0])
          .slice(0, 2)
          .join("")
          .toUpperCase();
        return (
          <li key={client.id}>
            <button
              type="button"
              onClick={() => onPick(t)}
              data-testid={`client-chat-thread-${client.id}`}
              className="w-full text-left p-3 flex items-start gap-3 hover:bg-[var(--mp-dash-bg)] transition-colors"
            >
              <div className="h-10 w-10 rounded-full bg-[var(--mp-yellow)] grid place-items-center font-display text-sm border border-[var(--mp-ink)] shrink-0">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display text-sm tracking-tight truncate">
                    {client.name_salone}
                  </div>
                  {last_message && (
                    <div className="text-[10px] uppercase tracking-[0.1em] text-[var(--mp-muted)] shrink-0">
                      {new Date(last_message.created_at).toLocaleString(
                        "it-IT",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          day: "2-digit",
                          month: "2-digit",
                        }
                      )}
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-[var(--mp-muted)] truncate mt-0.5">
                  {last_message ? (
                    <>
                      <span className="font-medium text-[var(--mp-ink)]">
                        {last_message.sender_role === "client"
                          ? client.referente_nome || "Cliente"
                          : "Tu"}
                        :
                      </span>{" "}
                      {last_message.text || "📎 allegato"}
                    </>
                  ) : (
                    <span className="italic">Nessun messaggio</span>
                  )}
                </div>
              </div>
              {unread > 0 && (
                <span
                  data-testid={`client-chat-thread-unread-${client.id}`}
                  className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--mp-primary)] text-white text-[10px] font-bold shrink-0"
                >
                  {unread}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
